import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CTAButtonLight } from '../ui/CTAButton';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 28
  },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay: i * 0.15,
      ease: 'easeOut'
    }
  })
};
// ─── Card 1: Donut / Radial Progress ─────────────────────────────────────────
function DonutCard() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.4
  });
  const [progress, setProgress] = useState(0);
  const pct = 70;
  const r = 52;
  const circ = 2 * Math.PI * r;
  const dash = progress / 100 * circ;
  useEffect(() => {
    if (!inView) return;
    let start: number | null = null;
    const animate = (ts: number) => {
      if (!start) start = ts;
      const elapsed = ts - start;
      const p = Math.min(elapsed / 1200 * pct, pct);
      setProgress(p);
      if (p < pct) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [inView]);
  return <motion.div ref={ref} custom={1} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="relative rounded-3xl overflow-hidden flex flex-col" style={{
    background: '#1A2B4A',
    minHeight: 380
  }} data-id="element-110">
      {/* Subtle grid texture */}
      <div className="absolute inset-0 opacity-20" style={{
      backgroundImage: 'linear-gradient(rgba(78,205,196,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(78,205,196,0.15) 1px, transparent 1px)',
      backgroundSize: '28px 28px'
    }} data-id="element-111" />
      <div className="relative z-10 p-7 flex flex-col flex-1" data-id="element-112">
        {/* Tag + pill */}
        <div className="flex items-center justify-between mb-5" data-id="element-113">
          <span className="text-[10px] font-black text-[#4ECDC4] uppercase tracking-[0.15em]" data-id="element-114">
            SCALABILITY UNLOCK
          </span>
          <span className="bg-[#4ECDC4] text-[#1A2B4A] text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wide" data-id="element-115">
            Life Insurance
          </span>
        </div>
        <p className="text-white/50 text-xs font-medium mb-6" data-id="element-116">Multi-Market</p>

        {/* Donut chart */}
        <div className="flex items-center justify-center flex-1 mb-6" data-id="element-117">
          <div className="relative" data-id="element-118">
            <svg width="140" height="140" viewBox="0 0 140 140" data-id="element-119">
              {/* Track */}
              <circle cx="70" cy="70" r={r} fill="none" stroke="rgba(78,205,196,0.12)" strokeWidth="14" data-id="element-120" />
              {/* Progress */}
              <circle cx="70" cy="70" r={r} fill="none" stroke="#4ECDC4" strokeWidth="14" strokeLinecap="round" strokeDasharray={`${dash} ${circ}`} strokeDashoffset={circ / 4} style={{
              filter: 'drop-shadow(0 0 8px rgba(78,205,196,0.6))',
              transition: 'stroke-dasharray 0.05s'
            }} data-id="element-121" />
              {/* Center text */}
              <text x="70" y="65" textAnchor="middle" fill="white" fontSize="26" fontWeight="900" data-id="element-122">
                {Math.round(progress)}%
              </text>
              <text x="70" y="84" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="10" fontWeight="600" data-id="element-123">
                HIRE RATE
              </text>
            </svg>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 gap-3" data-id="element-124">
          <div className="bg-white/8 rounded-xl p-3 text-center border border-white/10" data-id="element-125">
            <p className="text-lg font-black text-white" data-id="element-126">144</p>
            <p className="text-[10px] text-white/50 uppercase tracking-wider" data-id="element-127">
              Interviews
            </p>
          </div>
          <div className="bg-white/8 rounded-xl p-3 text-center border border-white/10" data-id="element-128">
            <p className="text-lg font-black text-[#4ECDC4]" data-id="element-129">101</p>
            <p className="text-[10px] text-white/50 uppercase tracking-wider" data-id="element-130">
              Hires
            </p>
          </div>
        </div>
      </div>
    </motion.div>;
}
// ─── Card 2: Animated Bar Chart ───────────────────────────────────────────────
function BarChartCard() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.4
  });
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    if (inView) setTimeout(() => setAnimated(true), 200);
  }, [inView]);
  const bars = [{
    label: 'Q1',
    height: 35,
    color: '#E8655A'
  }, {
    label: 'Q2',
    height: 58,
    color: '#E8655A'
  }, {
    label: 'Q3',
    height: 80,
    color: '#4ECDC4'
  }, {
    label: 'Q4',
    height: 100,
    color: '#4ECDC4'
  }];
  return <motion.div ref={ref} custom={2} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="relative rounded-3xl overflow-hidden flex flex-col" style={{
    background: '#F5F7FA',
    minHeight: 380
  }} data-id="element-131">
      {/* Top accent stripe */}
      <div className="h-1.5 w-full" style={{
      background: 'linear-gradient(90deg, #E8655A, #4ECDC4)'
    }} data-id="element-132" />

      <div className="p-7 flex flex-col flex-1" data-id="element-133">
        {/* Tag + pill */}
        <div className="flex items-center justify-between mb-2" data-id="element-134">
          <span className="text-[10px] font-black text-[#4ECDC4] uppercase tracking-[0.15em]" data-id="element-135">
            REVENUE JUMP
          </span>
          <span className="bg-[#1A2B4A] text-white text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wide" data-id="element-136">
            Windows
          </span>
        </div>
        <p className="text-[#1A2B4A]/40 text-xs font-medium mb-5" data-id="element-137">Western US</p>

        {/* Big stat */}
        <div className="mb-5" data-id="element-138">
          <p className="text-4xl font-black text-[#1A2B4A] leading-none" data-id="element-139">
            $2M+
          </p>
          <p className="text-xs text-[#1A2B4A]/50 font-semibold uppercase tracking-wider mt-1" data-id="element-140">
            New Revenue Added
          </p>
        </div>

        {/* Bar chart */}
        <div className="flex-1 flex items-end gap-3 mb-4" style={{
        minHeight: 100
      }} data-id="element-141">
          {bars.map(bar => <div key={bar.label} className="flex-1 flex flex-col items-center gap-1.5" data-id="element-142">
              <div className="w-full rounded-t-lg overflow-hidden" style={{
            height: 90,
            display: 'flex',
            alignItems: 'flex-end'
          }} data-id="element-143">
                <div className="w-full rounded-t-lg transition-all duration-1000 ease-out" style={{
              height: animated ? `${bar.height}%` : '0%',
              backgroundColor: bar.color,
              opacity: 0.85
            }} data-id="element-144" />
              </div>
              <span className="text-[10px] font-bold text-[#1A2B4A]/50" data-id="element-145">
                {bar.label}
              </span>
            </div>)}
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 gap-3 pt-4 border-t border-[#1A2B4A]/10" data-id="element-146">
          <div data-id="element-147">
            <p className="text-base font-black text-[#1A2B4A]" data-id="element-148">388</p>
            <p className="text-[10px] text-[#1A2B4A]/50 uppercase tracking-wider" data-id="element-149">
              Interviews
            </p>
          </div>
          <div data-id="element-150">
            <p className="text-base font-black text-[#4ECDC4]" data-id="element-151">250+</p>
            <p className="text-[10px] text-[#1A2B4A]/50 uppercase tracking-wider" data-id="element-152">
              Hires
            </p>
          </div>
        </div>
      </div>
    </motion.div>;
}
// ─── Card 3: Funnel Visualization ─────────────────────────────────────────────
function FunnelCard() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.4
  });
  const levels = [{
    label: 'Screened',
    value: 228,
    width: '100%',
    bg: 'rgba(78,205,196,0.15)',
    border: 'rgba(78,205,196,0.3)'
  }, {
    label: 'Interviewed',
    value: 114,
    width: '55%',
    bg: 'rgba(78,205,196,0.25)',
    border: 'rgba(78,205,196,0.45)'
  }, {
    label: 'Hired',
    value: 57,
    width: '30%',
    bg: '#4ECDC4',
    border: '#4ECDC4'
  }];
  return <motion.div ref={ref} custom={3} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="relative rounded-3xl overflow-hidden flex flex-col" style={{
    background: 'linear-gradient(145deg, #1A2B4A 0%, #0f1e35 100%)',
    minHeight: 380
  }} data-id="element-153">
      {/* Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-[#4ECDC4]/10 rounded-full blur-3xl pointer-events-none" data-id="element-154" />

      <div className="relative z-10 p-7 flex flex-col flex-1" data-id="element-155">
        {/* Tag + pill */}
        <div className="flex items-center justify-between mb-2" data-id="element-156">
          <span className="text-[10px] font-black text-[#4ECDC4] uppercase tracking-[0.15em]" data-id="element-157">
            ROOKIE RAMP-UP
          </span>
          <span className="bg-[#4ECDC4] text-[#1A2B4A] text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wide" data-id="element-158">
            Roofing
          </span>
        </div>
        <p className="text-white/40 text-xs font-medium mb-5" data-id="element-159">Atlanta</p>

        {/* Big stat */}
        <div className="mb-6" data-id="element-160">
          <p className="text-4xl font-black text-white leading-none" data-id="element-161">66%</p>
          <p className="text-xs text-white/50 font-semibold uppercase tracking-wider mt-1" data-id="element-162">
            Conversion Rate
          </p>
        </div>

        {/* Funnel */}
        <div className="flex-1 flex flex-col items-center gap-1 justify-center" data-id="element-163">
          {levels.map((level, i) => <motion.div key={level.label} initial={{
          width: 0,
          opacity: 0
        }} animate={inView ? {
          width: level.width,
          opacity: 1
        } : {}} transition={{
          duration: 0.7,
          delay: 0.3 + i * 0.2,
          ease: 'easeOut'
        }} className="rounded-lg flex items-center justify-between px-4 py-2.5" style={{
          backgroundColor: level.bg,
          border: `1px solid ${level.border}`,
          overflow: 'hidden'
        }} data-id="element-164">
              <span className={`text-xs font-semibold whitespace-nowrap ${i === levels.length - 1 ? 'text-[#1A2B4A]' : 'text-white/80'}`} data-id="element-165">
                {level.label}
              </span>
              <span className={`text-sm font-black ${i === levels.length - 1 ? 'text-[#1A2B4A]' : 'text-white'}`} data-id="element-166">
                {level.value}
              </span>
            </motion.div>)}
        </div>
      </div>
    </motion.div>;
}
// ─── Card 4: Radial Segments / Retention Ring ─────────────────────────────────
function RetentionCard() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.4
  });
  const [progress, setProgress] = useState(0);
  const pct = 78;
  const r = 48;
  const circ = 2 * Math.PI * r;
  const dash = progress / 100 * circ;
  useEffect(() => {
    if (!inView) return;
    let start: number | null = null;
    const animate = (ts: number) => {
      if (!start) start = ts;
      const elapsed = ts - start;
      const p = Math.min(elapsed / 1400 * pct, pct);
      setProgress(p);
      if (p < pct) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [inView]);
  const metrics = [{
    label: 'Interviews',
    value: 215,
    color: '#4ECDC4'
  }, {
    label: 'Hires',
    value: 142,
    color: '#1A2B4A'
  }, {
    label: 'Retention',
    value: '78%',
    color: '#4ECDC4'
  }];
  return <motion.div ref={ref} custom={4} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="relative rounded-3xl overflow-hidden flex flex-col" style={{
    background: '#4ECDC4',
    minHeight: 380
  }} data-id="element-167">
      {/* Subtle dot pattern */}
      <div className="absolute inset-0 opacity-20" style={{
      backgroundImage: 'radial-gradient(circle, #1A2B4A 1px, transparent 1px)',
      backgroundSize: '18px 18px'
    }} data-id="element-168" />

      <div className="relative z-10 p-7 flex flex-col flex-1" data-id="element-169">
        {/* Tag + pill */}
        <div className="flex items-center justify-between mb-2" data-id="element-170">
          <span className="text-[10px] font-black text-[#1A2B4A]/70 uppercase tracking-[0.15em]" data-id="element-171">
            RETENTION FIX
          </span>
          <span className="bg-[#1A2B4A] text-white text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wide" data-id="element-172">
            Solar
          </span>
        </div>
        <p className="text-[#1A2B4A]/60 text-xs font-medium mb-4" data-id="element-173">Southwest</p>

        {/* Donut */}
        <div className="flex items-center justify-center flex-1 mb-5" data-id="element-174">
          <div className="relative" data-id="element-175">
            <svg width="130" height="130" viewBox="0 0 130 130" data-id="element-176">
              <circle cx="65" cy="65" r={r} fill="none" stroke="rgba(26,43,74,0.15)" strokeWidth="12" data-id="element-177" />
              <circle cx="65" cy="65" r={r} fill="none" stroke="#1A2B4A" strokeWidth="12" strokeLinecap="round" strokeDasharray={`${dash} ${circ}`} strokeDashoffset={circ / 4} style={{
              transition: 'stroke-dasharray 0.05s'
            }} data-id="element-178" />
              <text x="65" y="60" textAnchor="middle" fill="#1A2B4A" fontSize="24" fontWeight="900" data-id="element-179">
                {Math.round(progress)}%
              </text>
              <text x="65" y="77" textAnchor="middle" fill="rgba(26,43,74,0.6)" fontSize="9" fontWeight="700" letterSpacing="1" data-id="element-180">
                RETENTION
              </text>
            </svg>
          </div>
        </div>

        {/* Metric tiles */}
        <div className="grid grid-cols-3 gap-2" data-id="element-181">
          {metrics.map(m => <div key={m.label} className="rounded-xl p-2.5 text-center" style={{
          backgroundColor: 'rgba(26,43,74,0.12)'
        }} data-id="element-182">
              <p className="text-base font-black text-[#1A2B4A]" data-id="element-183">{m.value}</p>
              <p className="text-[9px] font-bold text-[#1A2B4A]/60 uppercase tracking-wider" data-id="element-184">
                {m.label}
              </p>
            </div>)}
        </div>
      </div>
    </motion.div>;
}
// ─── Main Section ─────────────────────────────────────────────────────────────
export function CaseStudiesSection() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.1
  });
  return <section className="w-full bg-[#F5F7FA] py-24 px-6" data-id="element-185">
      <div ref={ref} className="max-w-6xl mx-auto" data-id="element-186">
        <motion.div custom={0} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="text-center mb-14" data-id="element-187">
          <span className="inline-block bg-[#4ECDC4] text-[#1A2B4A] text-xs font-bold uppercase px-3 py-1 rounded-full mb-4 tracking-wider" data-id="element-188">
            Case Studies
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-[#1A2B4A] mb-4 leading-tight" data-id="element-189">
            Math. Not Magic.
          </h2>
          <p className="text-lg text-[#1A2B4A]/60 max-w-xl mx-auto" data-id="element-190">
            Real companies. Real numbers. This is what happens when you install
            the HIRE Advantage.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12" data-id="element-191">
          <DonutCard data-id="element-192" />
          <BarChartCard data-id="element-193" />
          <FunnelCard data-id="element-194" />
          <RetentionCard data-id="element-195" />
        </div>

        <motion.div custom={5} variants={fadeUp} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="flex justify-center" data-id="element-196">
          <CTAButtonLight data-id="element-197" />
        </motion.div>
      </div>
    </section>;
}